<script>
import { useAlert } from 'dashboard/composables';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

export default {
  components: {
    EmojiOrIcon,
    NextButton,
    InlineInput,
  },
  props: {
    href: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      required: true,
    },
    emoji: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    showCopy: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
  },
  emits: ['update'],
  data() {
    return {
      isEditing: false,
      editValue: '',
    };
  },
  methods: {
    async onCopy(e) {
      e.preventDefault();
      await copyTextToClipboard(this.value);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    startEditing() {
      if (!this.editable) return;
      this.editValue = this.value || '';
      this.isEditing = true;
      this.$nextTick(() => {
        this.$refs.editInput?.focus();
      });
    },
    saveEdit() {
      if (!this.isEditing) return;
      this.isEditing = false;
      const trimmed = this.editValue.trim();
      if (trimmed !== (this.value || '')) {
        this.$emit('update', trimmed);
      }
    },
    cancelEdit() {
      this.isEditing = false;
    },
  },
};
</script>

<template>
  <div
    class="group/row w-full rounded-lg bg-n-slate-2/60 px-2.5 py-2 outline outline-1 outline-n-weak/80 -outline-offset-1"
  >
    <p
      class="mb-1 text-[11px] font-medium uppercase tracking-wide text-n-slate-9"
    >
      {{ title }}
    </p>

    <div v-if="isEditing" class="flex items-center gap-2 min-w-0">
      <InlineInput
        ref="editInput"
        v-model="editValue"
        :placeholder="title"
        class="!w-full min-w-0"
        @enter-press="saveEdit"
        @escape-press="cancelEdit"
        @blur="saveEdit"
      />
    </div>

    <div v-else-if="href" class="relative min-w-0">
      <a
        :href="href"
        class="flex items-start gap-2 min-w-0 text-n-slate-12 hover:text-n-blue-11 ltr:pr-14 rtl:pl-14"
      >
        <EmojiOrIcon
          :icon="icon"
          :emoji="emoji"
          icon-size="14"
          class="flex-shrink-0 mt-0.5"
        />
        <span
          v-if="value"
          class="flex-1 min-w-0 text-sm font-medium break-all leading-snug"
        >
          {{ value }}
        </span>
        <span v-else class="text-sm italic text-n-slate-9">
          {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
        </span>
      </a>
      <div
        class="absolute top-0 ltr:right-0 rtl:left-0 z-10 flex items-center gap-0.5 opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto transition-opacity"
      >
        <NextButton
          v-if="showCopy && value"
          v-tooltip="$t('CONTACT_PANEL.COPY_SUCCESSFUL')"
          ghost
          xs
          slate
          icon="i-lucide-clipboard"
          @click="onCopy"
        />
        <NextButton
          v-if="editable"
          v-tooltip.right="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
          ghost
          xs
          slate
          icon="i-lucide-pencil"
          @click.prevent="startEditing"
        />
      </div>
    </div>

    <div v-else class="relative min-w-0">
      <div class="flex items-start gap-2 min-w-0 ltr:pr-10 rtl:pl-10">
        <EmojiOrIcon
          :icon="icon"
          :emoji="emoji"
          icon-size="14"
          class="flex-shrink-0 mt-0.5"
        />
        <span
          v-if="value"
          v-dompurify-html="value"
          class="flex-1 min-w-0 text-sm font-medium break-words leading-snug text-n-slate-12"
        />
        <span v-else class="text-sm italic text-n-slate-9">
          {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
        </span>
      </div>
      <div
        v-if="editable"
        class="absolute top-0 ltr:right-0 rtl:left-0 z-10 flex items-center opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto transition-opacity"
      >
        <NextButton
          v-tooltip.right="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
          ghost
          xs
          slate
          icon="i-lucide-pencil"
          @click="startEditing"
        />
      </div>
    </div>
  </div>
</template>
